import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { BookOpen, Mail, User, Gift, Loader2, Phone } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ReaderRegister = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);
  const [showGiftCode, setShowGiftCode] = useState(false);
  const [giftCode, setGiftCode] = useState('');
  const [codeLoading, setCodeLoading] = useState(false);
  const [showLoginOption, setShowLoginOption] = useState(false);
  const [loginPassword, setLoginPassword] = useState('');

  // Check if user is already logged in
  useEffect(() => {
    const token = localStorage.getItem('reader_token');
    if (token) {
      // Verify token is still valid
      axios.get(`${API}/reader/progress`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then(() => {
        // Token is valid, redirect to biblioteca
        navigate('/reader/biblioteca');
      })
      .catch(() => {
        // Token is invalid, clear it
        localStorage.removeItem('reader_token');
        localStorage.removeItem('reader_user');
      });
    }
  }, [navigate]);
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Reset login option if email changes
    if (e.target.name === 'email') {
      setShowLoginOption(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Prepend +54 to phone if not already there
    const phoneNumber = formData.phone.startsWith('+54') ? formData.phone : `+54${formData.phone.replace(/\s/g, '')}`;

    try {
      const response = await axios.post(`${API}/reader/register`, {
        ...formData,
        phone: phoneNumber
      });
      
      // Guardar token
      localStorage.setItem('reader_token', response.data.token);
      localStorage.setItem('reader_user', JSON.stringify(response.data.user));

      toast.success('¡Registro exitoso! Te enviamos un email con tu contraseña.');
      
      // Redirigir al lector
      setTimeout(() => {
        navigate('/reader');
      }, 1500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrarse');
    } finally {
      setLoading(false);
    }
  };

  const handleGiftCodeSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error('Completá todos los campos antes de usar el código');
      return;
    }
    
    if (!giftCode.trim()) {
      toast.error('Ingresá un código');
      return;
    }

    setCodeLoading(true);
    
    // Prepend +54 to phone if not already there
    const phoneNumber = formData.phone.startsWith('+54') ? formData.phone : `+54${formData.phone.replace(/\s/g, '')}`;

    try {
      // First register the user
      const registerResponse = await axios.post(`${API}/reader/register`, {
        ...formData,
        phone: phoneNumber
      });
      
      // Save token
      localStorage.setItem('reader_token', registerResponse.data.token);
      localStorage.setItem('reader_user', JSON.stringify(registerResponse.data.user));

      // Then redeem the gift code
      const codeResponse = await axios.post(`${API}/reader/redeem-code`, 
        { code: giftCode.trim().toUpperCase() },
        { headers: { Authorization: `Bearer ${registerResponse.data.token}` } }
      );

      if (codeResponse.data.success) {
        toast.success('🎉 ¡Código canjeado! Libro completo desbloqueado');
        setTimeout(() => {
          navigate('/reader');
        }, 1500);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error al procesar';
      
      // Check if user already exists
      if (errorMessage.includes('ya está registrado')) {
        toast.error('Este email ya está registrado. Probá iniciar sesión.');
        setShowLoginOption(true);
      } else if (localStorage.getItem('reader_token')) {
        // Registration succeeded but code failed
        toast.error(errorMessage || 'Código inválido, pero tu cuenta fue creada');
        setTimeout(() => {
          navigate('/reader');
        }, 2000);
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setCodeLoading(false);
    }
  };

  const handleLogin = async () => {
    if (!formData.email || !loginPassword) {
      toast.error('Ingresá email y contraseña');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/reader/login`, {
        email: formData.email,
        password: loginPassword
      });
      
      localStorage.setItem('reader_token', response.data.token);
      localStorage.setItem('reader_user', JSON.stringify(response.data.user));
      
      // If there's a gift code, try to redeem it
      if (giftCode.trim()) {
        try {
          await axios.post(`${API}/reader/redeem-code`, 
            { code: giftCode.trim().toUpperCase() },
            { headers: { Authorization: `Bearer ${response.data.token}` } }
          );
          toast.success('🎉 ¡Código canjeado! Libro completo desbloqueado');
        } catch (codeError) {
          // Code might be already used or invalid, but login was successful
          if (codeError.response?.data?.detail?.includes('Ya tenés')) {
            toast.success('¡Ya tenés acceso completo al libro!');
          }
        }
      } else {
        toast.success('¡Bienvenido de vuelta!');
      }
      
      setTimeout(() => navigate('/reader'), 1000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Contraseña incorrecta');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-amber-50 flex items-center justify-center p-4">
      <div className="max-w-5xl w-full grid md:grid-cols-2 gap-8 items-center">
        {/* Lado Izquierdo - Portada del libro */}
        <div className="text-center">
          <div className="relative inline-block">
            <img 
              src="/images/book-cover.png" 
              alt="Cábala para principiantes - Agustín Mata"
              className="w-full max-w-md mx-auto rounded-lg shadow-2xl transform hover:scale-105 transition-transform duration-300"
            />
            <div className="absolute -bottom-4 -right-4 bg-amber-500 text-white px-4 py-2 rounded-full font-bold shadow-lg">
              30 páginas gratis
            </div>
          </div>
          
          {/* Info del libro */}
          <div className="mt-8 bg-white/80 backdrop-blur rounded-xl p-4 shadow-md max-w-sm mx-auto">
            <p className="text-2xl font-bold text-gray-900">📖 176 páginas</p>
            <p className="text-sm text-gray-600 mt-1">de conocimiento profundo sobre Cábala</p>
          </div>
          
          <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-600 mt-4">
            <span className="flex items-center gap-1">✓ Modo noche</span>
            <span className="flex items-center gap-1">✓ Marcadores</span>
            <span className="flex items-center gap-1">✓ Zoom</span>
          </div>
        </div>

        {/* Lado Derecho - Formulario */}
        <Card className="shadow-2xl">
          <CardContent className="p-8">
            <h3 className="text-2xl font-serif font-bold text-gray-900 mb-2 text-center">
              Empezá tu viaje ahora
            </h3>
            <p className="text-sm text-amber-700 text-center mb-6">
              📖 Leé las primeras 30 páginas del libro gratis
            </p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre completo *
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-3 text-gray-400" size={20} />
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Tu nombre"
                    required
                    className="pl-10 border-gray-300 focus:border-amber-600 focus:ring-amber-600"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 text-gray-400" size={20} />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="tu@email.com"
                    required
                    className="pl-10 border-gray-300 focus:border-amber-600 focus:ring-amber-600"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Teléfono *
                </label>
                <div className="flex gap-2">
                  <div className="flex items-center px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-700 font-medium min-w-[100px]">
                    {/* Argentina Flag SVG */}
                    <svg className="w-6 h-4 mr-2 rounded-sm" viewBox="0 0 27 18" xmlns="http://www.w3.org/2000/svg">
                      <rect fill="#74ACDF" width="27" height="18"/>
                      <rect fill="#FFFFFF" y="6" width="27" height="6"/>
                      <circle cx="13.5" cy="9" r="2.5" fill="#F6B40E"/>
                    </svg>
                    <span>+54</span>
                  </div>
                  <div className="relative flex-1">
                    <Phone className="absolute left-3 top-3 text-gray-400" size={20} />
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="11 1234 5678"
                      required
                      className="pl-10 border-gray-300 focus:border-amber-600 focus:ring-amber-600"
                      data-testid="phone-input"
                    />
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || codeLoading || showLoginOption}
                className="w-full bg-gradient-to-r from-amber-600 to-amber-800 hover:from-amber-700 hover:to-amber-900 text-white px-6 py-4 rounded-full font-semibold text-lg transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
              >
                {loading ? 'Registrando...' : 'Acceder Gratis'}
              </button>

              {/* Login Section - Shows when email already exists */}
              {showLoginOption && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-3">
                  <p className="text-sm text-amber-800 font-medium text-center">
                    Este email ya está registrado. Ingresá tu contraseña para continuar:
                  </p>
                  <Input
                    type="password"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    placeholder="Tu contraseña"
                    className="border-amber-300 focus:border-amber-600 focus:ring-amber-600"
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={handleLogin}
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white px-4 py-3 rounded-full font-semibold transition-all disabled:opacity-50"
                  >
                    {loading ? <Loader2 className="animate-spin mx-auto" size={20} /> : 'Iniciar Sesión'}
                  </button>
                  <p className="text-xs text-amber-700 text-center">
                    ¿Olvidaste tu contraseña? Revisá el email que recibiste al registrarte.
                  </p>
                </div>
              )}

              {/* Gift Code Section */}
              <div className="pt-4 border-t border-gray-200">
                {!showGiftCode ? (
                  <button
                    type="button"
                    onClick={() => setShowGiftCode(true)}
                    className="w-full text-amber-700 hover:text-amber-800 font-medium text-sm flex items-center justify-center gap-2 py-2"
                  >
                    <Gift size={18} />
                    ¿Tenés un código de regalo?
                  </button>
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-600 text-center">
                      Ingresá tu código para desbloquear el libro completo
                    </p>
                    <div className="flex gap-2">
                      <Input
                        type="text"
                        value={giftCode}
                        onChange={(e) => setGiftCode(e.target.value.toUpperCase())}
                        placeholder="CÓDIGO"
                        data-testid="register-gift-code"
                        className="flex-1 text-center font-mono uppercase tracking-wider border-amber-300 focus:border-amber-600 focus:ring-amber-600"
                        maxLength={20}
                      />
                      <button
                        type="button"
                        onClick={handleGiftCodeSubmit}
                        disabled={codeLoading || loading}
                        className="px-4 py-2 rounded-lg font-semibold bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white disabled:opacity-50 transition-all"
                      >
                        {codeLoading ? <Loader2 className="animate-spin" size={20} /> : 'Canjear'}
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowGiftCode(false)}
                      className="w-full text-gray-400 hover:text-gray-600 text-xs"
                    >
                      Cancelar
                    </button>
                  </div>
                )}
              </div>

              <p className="text-xs text-gray-500 text-center mt-4">
                Al registrarte, aceptás recibir emails con tu contraseña y actualizaciones del libro.
                Tu contraseña será generada automáticamente y enviada a tu email.
              </p>

              <div className="mt-4 pt-4 border-t border-gray-200 text-center">
                <p className="text-gray-600 text-sm">
                  ¿Ya tenés cuenta?{' '}
                  <button 
                    type="button"
                    onClick={() => navigate('/reader/login')}
                    className="text-amber-600 font-semibold hover:underline"
                  >
                    Iniciá sesión
                  </button>
                </p>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};